import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Text } from "@/atoms";
import { useTranslation } from "@/core/i18n";
import { useCountUp } from "@/hooks/useCountUp";
import { goToSignIn, useIsAnonymousBrowsing } from "@/modules/anonymousBrowsing";
import { ChevronRight } from "lucide-react";

import {
  LOYALTY_STATUS_PATH,
  LoyaltyTierBadge,
  LoyaltyTierChip,
  statusLine,
  useLoyaltyStatus,
} from "@/modules/loyalty";
import { RewardProgressBar } from "@/modules/onboarding/components/RewardProgressBar/RewardProgressBar";
import { getPlatform, getUser, getUserCheckIn } from "api/methods";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CheckSmall from "atoms/icon/icons/ic_check_small_ol.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import RepeatMD from "atoms/icon/icons/RepeatCash.svg";
import UserNav from "atoms/icon/icons/user_nav.svg";
import { formatCurrency } from "toolbox/Money";

const SCROLL_THRESHOLD = 5; // approximate height of banners

export type RewardProgressV2Props = {
  presentation?: "desktop-summary";
  rewardsCount: number;
  nextMilestone: number;
  nextMilestoneRewardsCount: number;
  unlockProgressPercent: number;
  cashToUnlock?: number;
  marketplaceEnabled?: boolean;
  visitCashValue?: number;
  spendBasedRewardsEnabled: boolean;
  hideRepeatCash?: boolean;
  displayGreetings?: boolean;
  canCollapse?: boolean;
  showCountUpAnimation?: boolean;
};

export const RewardProgressV2 = ({
  presentation,
  rewardsCount,
  nextMilestone,
  nextMilestoneRewardsCount,
  unlockProgressPercent,
  cashToUnlock,
  marketplaceEnabled,
  visitCashValue,
  spendBasedRewardsEnabled,
  hideRepeatCash = false,
  displayGreetings = false,
  canCollapse = false,
  showCountUpAnimation = false,
}: RewardProgressV2Props) => {
  const isDesktopSummary = presentation === "desktop-summary";
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isAnonymousBrowsing = useIsAnonymousBrowsing();
  const { data: userCanCheckIn } = useQuery({ ...getUserCheckIn.keyed() });

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const { data: platform } = useQuery({ ...getPlatform.keyed() });
  const checkInDisabled = platform?.platformSettings?.patientCheckInDisabled ?? false;
  const loyaltyStatus = useLoyaltyStatus({
    programEnabled: platform?.platformSettings?.loyaltyProgramEnabled === true,
    isSignedInPatient: !!user && !user.isGuest && !isAnonymousBrowsing,
  });
  const currentTier = loyaltyStatus?.currentTier;

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!canCollapse) return;
    const scrollContainer = document.getElementById("root");
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollPosition = scrollContainer.scrollTop;
      setIsScrolled(scrollPosition > SCROLL_THRESHOLD);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial scroll position
    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [canCollapse]);

  const repeatCashBalance = Number(user?.repeatCashBalance ?? 0);

  const countUpValue = useCountUp({
    to: repeatCashBalance,
    from: showCountUpAnimation ? 0 : repeatCashBalance,
    duration: 1500,
    delay: 0,
  });

  if (!user) {
    return null;
  }

  const userMessage = (): string => {
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    let greeting = t("rewardProgressV2.goodEvening");
    if (currentHour < 12) greeting = t("rewardProgressV2.goodMorning");
    else if (currentHour < 18) greeting = t("rewardProgressV2.goodAfternoon");
    if (user.firstName) {
      return t("rewardProgressV2.greetingWithName", {
        greeting,
        firstName: user.firstName,
      }).toUpperCase();
    }
    return greeting;
  };

  const getTitleText = (): string | undefined => {
    if (isAnonymousBrowsing) {
      return t("anonymousBrowsing.logInToEarnRewards");
    }

    if (user.isGuest) {
      return t("rewardProgressV2.guestTitle");
    }

    if (nextMilestone === 0 && rewardsCount === 0) {
      return undefined;
    }

    if (!marketplaceEnabled || visitCashValue === 0 || !spendBasedRewardsEnabled) {
      return t("rewardProgressV2.almostThereVisits", { count: nextMilestone });
    }

    if (nextMilestone) {
      return cashToUnlock
        ? t("rewardProgressV2.almostThereVisitsWithCash", {
            count: nextMilestone,
            cash: formatCurrency(cashToUnlock),
          })
        : t("rewardProgressV2.almostThereVisits", { count: nextMilestone });
    }

    if (rewardsCount > 0) {
      return t("rewardProgressV2.rewardsWaiting", { count: rewardsCount });
    }

    return undefined;
  };

  const renderRepeatCash = () => {
    if (hideRepeatCash || user.isGuest) {
      return null;
    }

    if (isDesktopSummary) {
      return (
        <Button style="white" onClick={() => navigate("/rewards")}>
          <span className="flex items-center gap-4 py-3 text-left">
            <Icon svg={RepeatMD} size="small" />
            <span className="flex flex-col gap-1">
              <span className="text-body2 text-secondary">{t("walletLayout.repeatCash")}</span>
              <span className="text-[28px] leading-tight text-primary">
                {formatCurrency(countUpValue, 0)}
              </span>
            </span>
          </span>
        </Button>
      );
    }

    return (
      <button
        className="flex items-center gap-2 rounded-full border border-white bg-transparent px-3 py-1 transition-colors hover:bg-white/10"
        type="button"
        onClick={() => navigate("/rewards")}
      >
        <Icon svg={RepeatMD} size="small" />
        <Text
          variant="body1"
          component="p"
          className="font-bold"
          text={formatCurrency(countUpValue, 0)}
        />
      </button>
    );
  };

  const renderYouHaveScannedBanner = () => (
    <div className="flex items-center gap-1 rounded-full bg-background-two py-2 pl-3 pr-3">
      <Icon svg={CheckSmall} color="secondaryText" size="x2small" />
      <Text
        variant="body3"
        component="p"
        className="text-secondary"
        text={t("rewardProgressV2.checkedInToday")}
      />
    </div>
  );

  const renderScanInButton = () => {
    const handleClick = () => {
      if (isAnonymousBrowsing) {
        goToSignIn(navigate, location.pathname + location.search);
        return;
      }
      navigate(user.isGuest ? "/signin" : "/scan-checkin-page");
    };
    const icon = user.isGuest ? UserNav : QrCode;
    const hasNextReward = nextMilestone > 0 || rewardsCount > 0;
    let buttonText = t("rewardProgressV2.checkInButton");
    if (isAnonymousBrowsing) {
      buttonText = t("anonymousBrowsing.logIn");
    } else if (user.isGuest) {
      buttonText = t("rewardProgressV2.signUpToEarnButton");
    } else if (hasNextReward) {
      buttonText = t("rewardProgressV2.checkInForRewardsButton");
    }

    return (
      <div className={cn("w-full", isAnonymousBrowsing && "desk:w-auto")}>
        <Button
          endIconSVG={icon}
          onClick={handleClick}
          size="small"
          style="white"
          iconSize="xsmall"
          fullWidth={isAnonymousBrowsing}
        >
          <p className="sans-serif mr-2 text-sm font-semibold">{buttonText}</p>
        </Button>
      </div>
    );
  };

  const titleText = getTitleText();
  const shouldHideProgress = nextMilestone === 0 && rewardsCount === 0 && !user.isGuest;

  // Desktop (not the anonymous banner or the summary) lays the tracker out as one band of two
  // aligned columns: status on the left, the next reward on the right. Phones keep the stack.
  const hasDesktopBand = !isAnonymousBrowsing && !isDesktopSummary;
  const renderGreeting = () => {
    const greeting = renderGreetingText();
    if (!currentTier) return greeting;
    return (
      <div
        className={cn(
          "flex flex-wrap items-center justify-center gap-x-2 gap-y-1",
          isDesktopSummary && "desk:justify-start",
          hasDesktopBand && "desk:col-start-1 desk:row-start-1 desk:justify-start",
        )}
      >
        {greeting}
        <span className={cn("flex", hasDesktopBand && "desk:hidden")}>
          <LoyaltyTierChip tier={currentTier} onClick={() => navigate(LOYALTY_STATUS_PATH)} />
        </span>
      </div>
    );
  };

  const renderGreetingText = () =>
    displayGreetings && (
      <Text
        variant="body1"
        component="p"
        className={cn(
          "font-bold text-brand-color",
          isDesktopSummary && "desk:text-sub2 desk:tracking-[0.08em]",
          hasDesktopBand &&
            "desk:col-start-1 desk:row-start-1 desk:flex desk:min-h-8 desk:items-center desk:text-body3 desk:font-semibold desk:tracking-[0.12em] desk:text-secondary",
        )}
        text={userMessage()}
      />
    );
  const desktopTierLine = loyaltyStatus && statusLine(t, loyaltyStatus);
  const renderDesktopTier = () =>
    currentTier && (
      <div className="hidden items-center gap-3 desk:col-start-1 desk:row-start-2 desk:flex">
        <LoyaltyTierBadge tier={currentTier} size="md" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate font-serif text-[28px] leading-tight text-primary">
            {currentTier.name}
          </p>
          {desktopTierLine && (
            <button
              type="button"
              className="flex items-center gap-0.5 text-left text-body2 font-semibold text-brand-color hover:underline"
              onClick={() => navigate(LOYALTY_STATUS_PATH)}
            >
              {desktopTierLine}
              <ChevronRight aria-hidden className="h-4 w-4 shrink-0" />
            </button>
          )}
        </div>
      </div>
    );

  return (
    <div
      className={cn(
        "RewardProgressV2 flex flex-col gap-3 px-5 pt-3 text-center shadow-lg desk:mx-auto desk:w-full desk:max-w-screen-xl desk:px-6 desk:shadow-none desklg:px-8 desk2xl:max-w-screen-2xl",
        shouldHideProgress && "pb-4",
        isAnonymousBrowsing
          ? "desk:max-w-4xl desk:flex-row desk:items-center desk:justify-center desk:gap-4 desk:py-3 desk:text-left"
          : "desk:flex-row desk:flex-wrap desk:items-center desk:justify-center desk:gap-x-12 desk:gap-y-2 desk:py-3 desk:text-left",
        isDesktopSummary &&
          "desk:grid desk:max-w-none desk:grid-cols-[minmax(0,1fr)_auto] desk:items-center desk:justify-start desk:gap-x-8 desk:gap-y-3 desk:px-8 desk:py-6 desk2xl:max-w-none",
        hasDesktopBand &&
          "desk:grid desk:grid-cols-[minmax(0,1fr)_1px_minmax(0,1.5fr)] desk:items-start desk:gap-x-10 desk:gap-y-3 desk:py-6",
      )}
    >
      {renderGreeting()}
      {hasDesktopBand && (
        <>
          {renderDesktopTier()}
          <span
            aria-hidden
            className="hidden self-stretch bg-outline-grey desk:col-start-2 desk:row-span-3 desk:row-start-1 desk:block"
          />
          <p
            className={cn(
              "hidden text-body3 font-semibold uppercase tracking-[0.12em] text-secondary",
              "desk:col-start-3 desk:row-start-1 desk:flex desk:min-h-8 desk:items-center",
            )}
          >
            {nextMilestone > 0
              ? t("rewardProgressV2.nextRewardLabel")
              : t("rewardProgressV2.yourRewardsLabel")}
          </p>
        </>
      )}
      <div
        className={cn(
          "flex items-center justify-between",
          isAnonymousBrowsing && "justify-center desk:order-3 desk:shrink-0",
          hasDesktopBand &&
            (currentTier
              ? "desk:col-start-3 desk:row-start-1 desk:justify-self-end"
              : "desk:col-start-1 desk:row-start-2 desk:justify-self-start"),
          isDesktopSummary && "desk:order-none desk:col-start-2 desk:row-span-2 desk:row-start-1",
        )}
      >
        {renderRepeatCash()}
        {!(checkInDisabled && !user.isGuest) && (
          <div className={cn(!isAnonymousBrowsing && "desk:hidden")}>
            {userCanCheckIn ? renderScanInButton() : renderYouHaveScannedBanner()}
          </div>
        )}
      </div>

      {!shouldHideProgress && (
        <>
          <div
            className={cn(
              "contents",
              hasDesktopBand &&
                "desk:col-start-3 desk:row-start-2 desk:flex desk:w-full desk:flex-col-reverse desk:gap-3",
              isDesktopSummary &&
                "desk:order-none desk:col-start-1 desk:row-start-2 desk:flex desk:w-full desk:max-w-2xl desk:flex-col-reverse desk:gap-3",
            )}
          >
            <div className={cn("w-full", isAnonymousBrowsing && "desk:hidden")}>
              <RewardProgressBar
                progress={unlockProgressPercent}
                className={cn(hasDesktopBand && "desk:h-3")}
              />
            </div>

            <motion.div
              animate={{
                height: canCollapse && isScrolled ? 0 : "auto",
                opacity: canCollapse && isScrolled ? 0 : 1,
              }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className={cn("overflow-hidden", isAnonymousBrowsing && "desk:order-2")}
            >
              <div className={cn("flex flex-col gap-2 pb-4", "desk:pb-0")}>
                {titleText && (
                  <Text
                    variant="v3-h4"
                    component="p"
                    className={cn(
                      "w-full text-center desk:w-auto desk:whitespace-nowrap desk:text-left",
                      hasDesktopBand &&
                        "desk:whitespace-normal desk:font-sans desk:text-[22px] desk:leading-snug",
                      isAnonymousBrowsing && "desk:w-auto desk:whitespace-nowrap",
                      isDesktopSummary &&
                        "desk:whitespace-normal desk:font-sans desk:text-[22px] desk:leading-snug",
                    )}
                    text={titleText}
                  />
                )}

                {nextMilestone > 0 && nextMilestoneRewardsCount > 1 && (
                  <Text
                    variant="body2"
                    component="p"
                    className="w-full text-center text-secondary"
                    text={t("rewardProgressV2.rewardsAtNextMilestone", {
                      count: nextMilestoneRewardsCount,
                    })}
                  />
                )}

                {!user.isGuest && checkInDisabled && (
                  <Text
                    variant="body2"
                    component="p"
                    className="w-full text-center text-secondary"
                    text={t("common.earnRewardsEveryVisit")}
                  />
                )}

                {!user.isGuest && spendBasedRewardsEnabled && !checkInDisabled && (
                  <Text
                    variant="body2"
                    component="p"
                    className="w-full text-center text-secondary desk:hidden"
                    text={t("rewardProgressV2.checkInEveryVisitPrompt")}
                  />
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </div>
  );
};
